import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Checkbox } from '../../../../../ui';
import { useStores } from '../../../../app/root/domain/stores/useStores';
import { useFlexMicroform } from '../../../../payment/ui/credit-card/form/helpers/useFlexMicroform';
import { PaymentMainUserShimmer } from '../payment/PaymentMainUserShimmer';
import { ErrorMessage } from '../payment/new-card/ErrorMessage';
import { usePaymentSubmitApp } from '../payment/helpers/usePaymentSubmit';
import { useNewCardFormContext } from '../payment/helpers/useNewCardFormContext';
import { CreditCardForm } from '../../../../payment/ui/credit-card/form';
import { useLocation } from '../../../../shared/router/useLocation';
import { usePaymentContext } from '../payment/PaymentContext';
import { useTranslation } from '../../../../shared/i18n/translate/useTranslation';
import { ApiErrorMessage } from '../../errors/ApiErrorMessage';

declare global {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	interface Window {
		submitPaymentForm: () => void;
	}
}

export const NewCardForApp = observer(() => {
	const form = useNewCardFormContext();
	const { t } = useTranslation();
	const { captureContextStore } = useStores();
	const { query } = useLocation();
	const { microform, error } = useFlexMicroform(captureContextStore);
	const { submit, paymentError, sendErrorToApp } = usePaymentSubmitApp(
		query?.callbackUrl,
	);
	const { storeCard, setStoreCard } = usePaymentContext();

	const canSaveParam = query?.canSave;
	const canSave = !!(
		typeof canSaveParam === 'string' && canSaveParam === 'true'
	);

	useEffect(() => {
		if (form) {
			// Add 'submitPaymentForm' to the Window object to let 'Pay' button to trigger that function
			// 'Pay' button is added on the app side
			window.submitPaymentForm = submitPaymentForm;
		}
	}, [submit, form]);

	if (!microform) {
		return <PaymentMainUserShimmer />;
	}

	if (error) {
		return <ErrorMessage />;
	}

	const submitPaymentForm = async () => {
		// trigger form validation
		await form.trigger();
		const hasErrors = Object.getOwnPropertyNames(form.formState.errors).length;

		if (hasErrors) {
			sendErrorToApp();
		} else {
			form.handleSubmit(submit)();
		}
	};

	const storeCardChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (setStoreCard) {
			setStoreCard(event.target.checked);
		}
	};

	return (
		<StyledNewCardForAppForm>
			<CreditCardForm />
			{canSave && (
				<Checkbox
					checked={storeCard}
					label={t('payment_form_save_credit_card_label')}
					onChange={storeCardChange}
				/>
			)}
			{paymentError && (
				<ApiErrorMessage
					error={paymentError}
					genericMessage="parking_payment_generic_error_message"
				/>
			)}
		</StyledNewCardForAppForm>
	);
});

const StyledNewCardForAppForm = styled.form`
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding: 0 16px;

	div {
		margin-bottom: 0;
	}
`;
